import { DateTime } from "luxon";
import { UserLabel } from "../services/entourance-data.service";
import { PGLocationAddress } from "../models/map.model";

export class PGUtilities {
    static tryParseJSON(val:any, silent?:boolean) {
        if(typeof val == 'string') {
            try {
                return JSON.parse(val);
            }
            catch(ex) {
                if(!silent) console.log(ex)
            }

            return null;
        }
        else {
            return val;
        }
    }

    static getRandomId(length?:number) {
        if(length == null) length = 8;

        let _retVal = '';

        for(let i = 0; i < length; i++) {
            _retVal += Math.floor(Math.random() * 16).toString(16);
        }

        return _retVal;
    }

    static shuffleArray(array:Array<any>) {
        for(let i = 0; i < array.length - 1; i++) {
            let _randomI = i + 1 + Math.floor(Math.random() * (array.length - i - 1))

            let _swap = array[i];
            array[i] = array[_randomI]
            array[_randomI] = _swap;
        }
    }

    static loadScript(url:string, attrs?:{[name:string]:string}) {
        return new Promise((resolve, reject) => {
            let _script = document.createElement('script');
            _script.type = 'text/javascript';
    
            if(attrs != null) {
                for(let i in attrs) {
                    _script.setAttribute(i, attrs[i]);
                }
            }
    
            _script.src = url;
            _script.onload = () => {
                resolve(null);
            };
            _script.onerror = (error: any) => reject();
            document.getElementsByTagName('head')[0].appendChild(_script);
        })
    }

    static parseAddressComponents(data:Array<google.maps.GeocoderAddressComponent>, timezone?:string):PGLocationAddress {
        let _addressComponents = {
            street_number: null,
            route: null,
            locality: null,
            administrative_area_level_3: null,
            administrative_area_level_2: null,
            administrative_area_level_1: null,
            country: null,
            postal_code: null
        }

        for(let _component of data) {
            for(let _componentType of _component.types) {
                if(typeof _addressComponents[_componentType] != 'undefined') {
                    _addressComponents[_componentType] = _component.short_name;
                }
            }
        }

        let _streetAddress = null;
        if(_addressComponents.route != null) {
            _streetAddress = _addressComponents.route; 
            if(_addressComponents.street_number != null) _streetAddress += ' ' + _addressComponents.street_number;
        }

        return {
            country: _addressComponents.country,
            region: _addressComponents.administrative_area_level_1,
            province: _addressComponents.administrative_area_level_2,
            city: _addressComponents.administrative_area_level_3 || _addressComponents.locality,
            locality: _addressComponents.locality,
            address: _streetAddress,
            zipcode: _addressComponents.postal_code,
            timezone: timezone
        }
    }

    /** ISO 3166-1 alpha-2 → alpha-3 (Viaggiare Sicuri uses 3-letter codes). */
    static countryAlpha2ToAlpha3(alpha2:string):string {
        if(!alpha2) return null;
        const code = alpha2.trim().toUpperCase();
        if(code.length === 3) return code;
        return PGUtilities._countryAlpha2ToAlpha3[code] || null;
    }

    static countryAlpha3ToAlpha2(alpha3:string):string {
        if(!alpha3) return null;
        const code = alpha3.trim().toUpperCase();
        if(code.length === 2) return code;
        for(const alpha2 in PGUtilities._countryAlpha2ToAlpha3) {
            if(PGUtilities._countryAlpha2ToAlpha3[alpha2] === code) return alpha2;
        }
        return null;
    }

    private static readonly _countryAlpha2ToAlpha3:{ [alpha2:string]: string } = {
        AD:'AND', AE:'ARE', AF:'AFG', AG:'ATG', AI:'AIA', AL:'ALB', AM:'ARM', AO:'AGO', AQ:'ATA', AR:'ARG',
        AS:'ASM', AT:'AUT', AU:'AUS', AW:'ABW', AX:'ALA', AZ:'AZE', BA:'BIH', BB:'BRB', BD:'BGD', BE:'BEL',
        BF:'BFA', BG:'BGR', BH:'BHR', BI:'BDI', BJ:'BEN', BL:'BLM', BM:'BMU', BN:'BRN', BO:'BOL', BQ:'BES',
        BR:'BRA', BS:'BHS', BT:'BTN', BV:'BVT', BW:'BWA', BY:'BLR', BZ:'BLZ', CA:'CAN', CC:'CCK', CD:'COD',
        CF:'CAF', CG:'COG', CH:'CHE', CI:'CIV', CK:'COK', CL:'CHL', CM:'CMR', CN:'CHN', CO:'COL', CR:'CRI',
        CU:'CUB', CV:'CPV', CW:'CUW', CX:'CXR', CY:'CYP', CZ:'CZE', DE:'DEU', DJ:'DJI', DK:'DNK', DM:'DMA',
        DO:'DOM', DZ:'DZA', EC:'ECU', EE:'EST', EG:'EGY', EH:'ESH', ER:'ERI', ES:'ESP', ET:'ETH', FI:'FIN',
        FJ:'FJI', FK:'FLK', FM:'FSM', FO:'FRO', FR:'FRA', GA:'GAB', GB:'GBR', GD:'GRD', GE:'GEO', GF:'GUF',
        GG:'GGY', GH:'GHA', GI:'GIB', GL:'GRL', GM:'GMB', GN:'GIN', GP:'GLP', GQ:'GNQ', GR:'GRC', GS:'SGS',
        GT:'GTM', GU:'GUM', GW:'GNB', GY:'GUY', HK:'HKG', HM:'HMD', HN:'HND', HR:'HRV', HT:'HTI', HU:'HUN',
        ID:'IDN', IE:'IRL', IL:'ISR', IM:'IMN', IN:'IND', IO:'IOT', IQ:'IRQ', IR:'IRN', IS:'ISL', IT:'ITA',
        JE:'JEY', JM:'JAM', JO:'JOR', JP:'JPN', KE:'KEN', KG:'KGZ', KH:'KHM', KI:'KIR', KM:'COM', KN:'KNA',
        KP:'PRK', KR:'KOR', KW:'KWT', KY:'CYM', KZ:'KAZ', LA:'LAO', LB:'LBN', LC:'LCA', LI:'LIE', LK:'LKA',
        LR:'LBR', LS:'LSO', LT:'LTU', LU:'LUX', LV:'LVA', LY:'LBY', MA:'MAR', MC:'MCO', MD:'MDA', ME:'MNE',
        MF:'MAF', MG:'MDG', MH:'MHL', MK:'MKD', ML:'MLI', MM:'MMR', MN:'MNG', MO:'MAC', MP:'MNP', MQ:'MTQ',
        MR:'MRT', MS:'MSR', MT:'MLT', MU:'MUS', MV:'MDV', MW:'MWI', MX:'MEX', MY:'MYS', MZ:'MOZ', NA:'NAM',
        NC:'NCL', NE:'NER', NF:'NFK', NG:'NGA', NI:'NIC', NL:'NLD', NO:'NOR', NP:'NPL', NR:'NRU', NU:'NIU',
        NZ:'NZL', OM:'OMN', PA:'PAN', PE:'PER', PF:'PYF', PG:'PNG', PH:'PHL', PK:'PAK', PL:'POL', PM:'SPM',
        PN:'PCN', PR:'PRI', PS:'PSE', PT:'PRT', PW:'PLW', PY:'PRY', QA:'QAT', RE:'REU', RO:'ROU', RS:'SRB',
        RU:'RUS', RW:'RWA', SA:'SAU', SB:'SLB', SC:'SYC', SD:'SDN', SE:'SWE', SG:'SGP', SH:'SHN', SI:'SVN',
        SJ:'SJM', SK:'SVK', SL:'SLE', SM:'SMR', SN:'SEN', SO:'SOM', SR:'SUR', SS:'SSD', ST:'STP', SV:'SLV',
        SX:'SXM', SY:'SYR', SZ:'SWZ', TC:'TCA', TD:'TCD', TF:'ATF', TG:'TGO', TH:'THA', TJ:'TJK', TK:'TKL',
        TL:'TLS', TM:'TKM', TN:'TUN', TO:'TON', TR:'TUR', TT:'TTO', TV:'TUV', TW:'TWN', TZ:'TZA', UA:'UKR',
        UG:'UGA', UM:'UMI', US:'USA', UY:'URY', UZ:'UZB', VA:'VAT', VC:'VCT', VE:'VEN', VG:'VGB', VI:'VIR',
        VN:'VNM', VU:'VUT', WF:'WLF', WS:'WSM', YE:'YEM', YT:'MYT', ZA:'ZAF', ZM:'ZMB', ZW:'ZWE',
    }

    static phoneRegExp = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/;
    static phoneInternationalRegExp = /^[+]+[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/;

    static emailRegExp = /^[a-zA-Z0-9\._%+!$&*=^|~#%'`?{}/\-]+@([a-zA-Z0-9\-]+\.){1,}([a-zA-Z]{2,16})$/

    static urlRegExp = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/

    static formatUserLabel(user:UserLabel, withId?:boolean) {
        let _retVal = ''

        if(user.name) _retVal += user.name;

        if(user.surname) {
            if(_retVal != '') _retVal += ' ';
            _retVal += user.surname;
        } 

        if(withId) {
            if(_retVal != '') {
                _retVal = user.id + ' - ' + _retVal;
            }
            else {
                _retVal = user.id;
            }
        }

        return _retVal;
    }

    static cleanPhone(phone:string) {
        if(phone == null) return null;
        else {
            phone = phone.replace(/[^\d|\+]/g, '')
            phone = phone.replace(/^00/, '+')

            if(phone == '') return null
            else {
                if(!/^\+/.test(phone)) {
                    phone = '+39' + phone;
                }
        
                return phone;
            }
        }
    }

    static cleanRecipient(recipient:string, via?:'phone'|'email'|'list') {
        if(recipient == null) return null;
        else {
            let _isComposite = false;

            if(via == null) {
                via = recipient.split('/')[0] as 'phone'|'email'|'list';
                recipient = recipient.replace(/^[^\/]*\//, '')
                _isComposite = true;
            }
    
            if(via == 'phone') {
                recipient = this.cleanPhone(recipient)
            }

            if(recipient == '') return null;
    
            if(_isComposite) return via + '/' + recipient
            else return recipient;
        }
    }

    static cleanEmbedSrc(src:string) {
        if(src != null) {
            return src.replace(/^.*"http/, 'http').replace(/".*$/, '');
        }
    }

    static cleanTranslation(translation:any) {
        let _result:any = {}

        for(let i in translation) {
            if(i != 'id' && !/_id$/.test(i) && i != 'created_at' && i != 'updated_at' && i != 'deleted_at' && i != 'language' && i != 'auto') {
                _result[i] = translation[i];
            }
        }

        return _result;
    }

    static cleanDateTime(datetime:Date|string) {
        if(datetime != null) {
            if(datetime instanceof Date) return DateTime.fromISO(datetime.toISOString()).toISO()
            else return DateTime.fromISO(datetime).toUTC().toISO()
        }
    }

    static deleteFromObject(target:any, toDelete:Array<string|RegExp>) {
        for(let _prop of toDelete) {
            if(typeof _prop == 'string') {
                if(typeof target[_prop] != 'undefined') {
                    delete target[_prop]
                }
            }
            else {
                let _keys = Object.keys(target)
                
                for(let i in _keys) {
                    if(_prop.test(i)) {
                        delete target[i]
                    }
                }
            }            
        }
    }

    static createElementCopy(resource:string, data:any) {
        data = JSON.parse(JSON.stringify(data))
        
        this.deleteFromObject(data, ['created_at','updated_at','deleted_at','id','user','user_id','published','exported',/^Connected/,'realm_id','slug','last_update_user_id','system_tag_labels'])

        if(data.translations != null) {
            for(let _item of data.translations) {
                this.deleteFromObject(_item, ['created_at','updated_at','deleted_at','id',/_id$/,'auto'])
            }
        }
        
        return data;
    }

    static resolveObjectPath(object:any, path:string) {
        let _split = path.split('.')

        for(let i = 0; i < _split.length; i++) {
            if(object != null) object = object[_split[i]];
        }

        return object;
    }

    static escapeRegExp(value:string) {
        if(value != null) {
            return value.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&")
        }
    }

    static parseCommaSeparatedList(value:string) {
        if(value != null) {
            return value.replace(/^\s*/, '').replace(/\s*$/, '').replace(/^,/, '').replace(/,$/, '').replace(/,+/, ',').split(/\s*,\s*/);
        }
    }

    static stripHTML(value:string) {
        let _element = document.createElement('div')
        _element.innerHTML = value
        return _element.innerText;
    }

    static getElementLastPublished(element:any, connector:string) {
        if(element != null && element.exported != null) {
            let _data = PGUtilities.tryParseJSON(element.exported)
            
            if(_data != null) {
                let _date = _data[connector.toLowerCase()]?.date

                if(_date != null) {
                    let _parsed = DateTime.fromFormat(_date, 'yyyy-LL-dd HH:mm:ss', { zone: 'utc' }).toJSDate()
                    if(!isNaN(_parsed.getTime())) return _parsed.toISOString();
                    else {
                        _parsed = new Date(_date)
                        if(!isNaN(_parsed.getTime())) return _parsed.toISOString();
                    }
                }
            }
        }
    }

    static cleanSlug(val:string) {
        if(!val) return ''
        else {
            return val.normalize('NFD') // scoppia accenti à > a` 
                .replace(/[\u0300-\u036f]/g, '') // toglie accenti a` > a 
                .toLowerCase()
                .replace(/[^\w\s-]/g, '')
                .trim()
                .replace(/[\s_-]+/g, '_')
        }
    }

    private static _validColorClass = [
        'primary',
        'secondary',
        'success',
        'danger',
        'warning',
        'info',
        'light',
        'dark',
        'white'
    ]

    static isValidColorClass(val:string) {
        return this._validColorClass.indexOf(val) != -1;
    }
}